import { EditorView, Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./live-preview/cursor";
import {
  autocompletion,
  acceptCompletion,
  startCompletion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete";
import { keymap } from "@codemirror/view";

// --- Wikilink Decorations (integrated into live preview pipeline) ---

const wikilinkPattern = /\[\[([^\]\n]+)\]\]/g;
const wikilinkTitleMark = Decoration.mark({ class: "cm-wikilink-title" });

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

/**
 * Build decorations for [[wikilinks]].
 *
 * Uses the same pattern as buildLinkDecorations:
 * - When cursor is on the same line, show raw [[Title]]
 * - Otherwise, hide [[ and ]] and style title as a pill
 *
 * Called from the live preview plugin alongside other decoration builders.
 */
export function buildWikilinkDecorations(
  state: EditorState,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  const cursorLine = cursor.cursorLine;
  const text = state.doc.sliceString(range.from, range.to);

  let match: RegExpExecArray | null;
  wikilinkPattern.lastIndex = 0;

  while ((match = wikilinkPattern.exec(text)) !== null) {
    const start = range.from + match.index;
    const end = start + match[0].length;
    const nodeLine = state.doc.lineAt(start).number;

    // If cursor is on this line, show raw markdown (no decorations)
    if (cursorLine === nodeLine) continue;

    // Hide [[
    decorations.push({
      from: start,
      to: start + 2,
      decoration: Decoration.replace({}),
    });

    // Style title as pill
    decorations.push({
      from: start + 2,
      to: end - 2,
      decoration: wikilinkTitleMark,
    });

    // Hide ]]
    decorations.push({
      from: end - 2,
      to: end,
      decoration: Decoration.replace({}),
    });
  }
}

// --- Click Handler ---

/**
 * The raw title of the [[wikilink]] covering `offset` in `lineText`, or null.
 * `offset` is relative to the line; the brackets count as part of the link.
 */
export function wikilinkTitleAt(lineText: string, offset: number): string | null {
  const re = new RegExp(wikilinkPattern.source, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(lineText)) !== null) {
    if (offset >= match.index && offset <= match.index + match[0].length) return match[1];
  }
  return null;
}

/**
 * Ctrl/Cmd+click on a wikilink pill navigates to the linked page.
 * Also shows pointer cursor + underline on Cmd/Ctrl hover.
 *
 * Wikilink contract: the markup is [[Page Title]] and the SERVER resolves the
 * title to a page (case-insensitive, trimmed, with the rename fallback — see
 * WikiService.ResolveTitle). This package never derives a slug: it dispatches
 * the title, and shared/wiki-nav.ts resolves it against the editor's wiki
 * (data-wiki-resolve-url) and runs the same save-gated boosted navigation as a
 * sidebar click. Kept as an event so this reusable editor package doesn't
 * depend on the wiki page's bundle.
 */
export const wikilinkClickHandler = EditorView.domEventHandlers({
  mousedown(event: MouseEvent, view: EditorView) {
    if (!(event.ctrlKey || event.metaKey)) return false;

    const target = event.target as HTMLElement;
    if (!target.closest(".cm-wikilink-title")) return false;

    const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
    if (pos === null) return false;

    const line = view.state.doc.lineAt(pos);
    const title = wikilinkTitleAt(line.text, pos - line.from);
    if (title === null) return false;

    event.preventDefault();
    view.dom.dispatchEvent(new CustomEvent("wiki-open-link", { bubbles: true, detail: { title } }));
    return true;
  },
});

// --- Autocomplete ---

/**
 * The search endpoint of the wiki this editor belongs to, published
 * server-side on the wiki editor root from the same scope as data-wiki-base
 * (studio workspace wiki: /api/assistant/wiki/search, org wiki:
 * /organizations/{slug}/api/assistant/wiki/search). Null outside a wiki.
 */
function wikiSearchUrl(view: EditorView | undefined): string | null {
  const root = view?.dom.closest<HTMLElement>("[data-wiki-search-url]");
  return root?.dataset.wikiSearchUrl || null;
}

const searchCache: Map<string, { titles: string[]; ts: number }> = new Map();
const CACHE_TTL = 10000;

async function fetchWikiTitles(searchUrl: string, query: string): Promise<string[]> {
  const url = `${searchUrl}?q=${encodeURIComponent(query)}`;
  const cached = searchCache.get(url); // keyed by full URL: one cache per wiki
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.titles;
  }

  try {
    const resp = await fetch(url);
    if (!resp.ok) return [];
    const results = await resp.json();
    const titles = results.map((r: { title: string }) => r.title);
    searchCache.set(url, { titles, ts: Date.now() });
    return titles;
  } catch {
    return [];
  }
}

export async function wikilinkCompletions(
  context: CompletionContext,
): Promise<CompletionResult | null> {
  const line = context.state.doc.lineAt(context.pos);
  const textBefore = line.text.slice(0, context.pos - line.from);

  const bracketIndex = textBefore.lastIndexOf("[[");
  if (bracketIndex === -1) return null;

  const afterBracket = textBefore.slice(bracketIndex + 2);
  if (afterBracket.includes("]]")) return null;

  // No wiki scope → no suggestions (never query some other wiki).
  const searchUrl = wikiSearchUrl(context.view);
  if (!searchUrl) return null;

  const query = afterBracket;
  const from = line.from + bracketIndex + 2;

  const titles = await fetchWikiTitles(searchUrl, query);
  if (titles.length === 0) return null;

  return {
    from,
    to: context.pos,
    // Insert the server's page title verbatim: [[Title]] is the link markup
    // the server resolves (SyncLinks / ResolveTitle).
    options: titles.map((title) => ({
      label: title,
      apply: title + "]]",
      type: "text",
    })),
    filter: true,
  };
}

export const wikilinkAutocomplete = autocompletion({
  override: [wikilinkCompletions],
  activateOnTyping: true,
});

export const wikilinkKeymap = keymap.of([{ key: "Tab", run: acceptCompletion }]);

export function insertWikilink(view: EditorView): void {
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: "[[" },
    selection: { anchor: from + 2 },
  });
  setTimeout(() => startCompletion(view), 50);
}

// --- Extensions (without decoration plugin — that's in live-preview now) ---

/**
 * Wikilink extensions: click handler + autocomplete + keymap.
 * Decoration is handled by the live preview plugin via buildWikilinkDecorations.
 */
export const wikilinkExtensions = [wikilinkClickHandler, wikilinkKeymap];
